import { SearchReceivablesHistoryRes_Result as SearchReceivablesHistoryResult } from "gen/search/service/service_pb"
import { Receivable_Transaction_Type as RecvTxnType } from "gen/txn/models_pb"
import { PrinterIcon } from "lucide-react"
import { useState } from "react"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { Format } from "shared-ts/classes/timestamp"
import { UUID } from "shared-ts/classes/uuid"
import { useEnumFilter } from "shared-ts/hooks/useFilter"
import { ReceivableKind, ReceivableKinds } from "shared-ts/receivables"
import { ensure } from "shared-ts/utils"
import { ChipColor } from "shared-web/components/base/informational/Chip/Chip"
import { useModal } from "shared-web/components/base/overlay/Modal/ModalContext"
import {
  BlockTableControl,
  BlockTableControlActions,
  BlockTableControlSearch,
} from "shared-web/components/composite/TableV2/BlockTableControl/BlockTableControl"
import { createColumnHelper } from "shared-web/components/composite/TableV2/createColumnHelper"
import { FilterGroup } from "shared-web/components/composite/TableV2/FilterGroup/FilterGroup"
import { HideColumnsButton } from "shared-web/components/composite/TableV2/HideColumns/HideColumnsButton/HideColumnsButton"
import { useHideColumns } from "shared-web/components/composite/TableV2/HideColumns/useHideColumns"
import { Table } from "shared-web/components/composite/TableV2/Table"
import {
  TableCellHeight,
  TableCellWidth,
} from "shared-web/components/composite/TableV2/TableCell"
import { TableScrollContainer } from "shared-web/components/composite/TableV2/TableScrollContainer"
import { useTable } from "shared-web/components/composite/TableV2/useTable"
import { toast } from "shared-web/lib/toast"
import { WebUrl } from "shared-web/lib/webUrl"

import {
  useLazyPrintReceivablesHistory,
  useSearchReceivablesHistory,
} from "features/api/search"
import { ClickableBalance } from "features/pos/receivables/ReceivableHistory/ClickableBalance"
import {
  ReceivableHistoryModal,
  receivableHistoryModalId,
} from "features/pos/receivables/ReceivableHistory/ReceivableHistoryModal"
import { usePurchaseOrderFilter } from "hooks/filters"
import { useWebFilter } from "hooks/filters/useWebFilter"
import { printPDF } from "lib/printPDF"
import { urls } from "urls"

interface ReceivablesHistoryPanelProps {
  customerId: UUID
  isParent: boolean
}

export function ReceivablesHistoryPanel({
  customerId,
  isParent,
}: ReceivablesHistoryPanelProps) {
  const historyModal = useModal(receivableHistoryModalId)
  const [balanceHistoryLedgerEntryId, setBalanceHistoryLedgerEntryId] =
    useState<UUID | null>(null)

  function openHistoryModal(ledgerEntryId: UUID) {
    setBalanceHistoryLedgerEntryId(ledgerEntryId)
    historyModal.open()
  }

  const { filters, selectedPurchaseOrderIds, selectedTypes } =
    useHistoryTableFilters(customerId)

  const search = useSearchReceivablesHistory({
    customerId: customerId,
    purchaseOrderIds: selectedPurchaseOrderIds,
    transactionTypes: selectedTypes,
  })

  const {
    mutateAsync: printReceivablesHistory,
    isPending: isPrintingReceivablesHistory,
  } = useLazyPrintReceivablesHistory()

  const hideColumnsProps = useHideColumns()

  const { tableProps, searchInputProps } = useTable({
    data: search.data?.pages.flatMap((p) => p.results) ?? [],
    columns: historyTableColumns(isParent, openHistoryModal),
    cellHeight: TableCellHeight.Small,
    extractRowKey: (row) => UUID.idString(row.receivable?.id),
    isLoading: search.isLoading,
    isError: search.isError,
    onLastRowInView: search.loadNext,
    hiddenColumns: hideColumnsProps.hiddenColumns,
    toggleColumnHideShow: hideColumnsProps.toggleColumnHideShow,
    enableKeyboardNavigation: true,
  })

  async function handlePrintReceivablesHistory() {
    try {
      const pdfBytes = await printReceivablesHistory({
        customerId: customerId,
        purchaseOrderIds: selectedPurchaseOrderIds,
        query: search.query,
        transactionTypes: selectedTypes,
      })
      printPDF(pdfBytes.pdfBytes)
    } catch (error: unknown) {
      toast.error({ error, message: "Something went wrong. Please try again." })
    }
  }

  return (
    <div className="h-[50vh]">
      <BlockTableControl
        searchInput={
          <BlockTableControlSearch
            searchQuery={search.query}
            onSearchQueryChange={search.setQuery}
            {...searchInputProps}
          />
        }
        actions={
          <BlockTableControlActions
            actions={[
              {
                label: "Print",
                onClick: handlePrintReceivablesHistory,
                mode: "secondary",
                loading: isPrintingReceivablesHistory,
                icon: PrinterIcon,
              },
            ]}
          />
        }
        filters={<FilterGroup filters={filters} />}
        hideShowColumns={
          <HideColumnsButton
            {...hideColumnsProps}
            columns={historyTableColumns(isParent, openHistoryModal)}
          />
        }
      />
      <TableScrollContainer>
        <Table {...tableProps} />
      </TableScrollContainer>
      {balanceHistoryLedgerEntryId && (
        <ReceivableHistoryModal ledgerEntryId={balanceHistoryLedgerEntryId} />
      )}
    </div>
  )
}

const colHelper = createColumnHelper<SearchReceivablesHistoryResult>()

function historyTableColumns(
  isParent: boolean,
  openHistoryModal: (ledgerEntryId: UUID) => void
) {
  return [
    colHelper.timestamp(
      ({ receivable }) => receivable?.date,
      Format.DATE_TIME_SHORT,
      {
        title: "Date",
        width: TableCellWidth.Medium,
      }
    ),
    colHelper.timestamp(
      ({ receivable }) => receivable?.invoice?.dueDate,
      Format.DATE_SHORT,
      {
        title: "Due",
        width: TableCellWidth.Small,
      }
    ),
    colHelper.accessor(
      ({ receivable }) =>
        receivable?.daysToPay
          ? Decimal.fromPB(receivable?.daysToPay).toFixed(0)
          : undefined,
      {
        title: "Days to Pay",
        width: TableCellWidth.Small,
      }
    ),
    colHelper.id({
      accessorFn: ({ receivable }) => receivable?.transaction?.identifier,
      chipColor: ({ receivable }) =>
        getTransactionDetails(
          ensure(receivable?.transaction?.type, "Missing receivable.type")
        ).color,
      path: ({ receivable }) => {
        return getTransactionDetails(
          ensure(
            receivable?.transaction?.type,
            "Missing receivable.transaction.type"
          ),
          UUID.fromPBOrThrow(
            receivable?.transaction?.id,
            "Missing receivable.transaction.id"
          )
        ).href
      },
    }),
    colHelper.accessor(
      ({ receivable }) =>
        getTransactionDetails(
          ensure(receivable?.transaction?.type, "Missing receivable.type")
        ).type,
      {
        title: "Type",
        width: TableCellWidth.Medium,
      }
    ),
    ...(isParent
      ? [
          colHelper.id({
            accessorFn: ({ receivable }) =>
              receivable?.job?.id ? receivable?.job?.identifier : undefined,
            column: { title: "Job", width: TableCellWidth.Small },
            chipColor: "sky",
            path: ({ receivable }) => urls.job(receivable?.job?.id),
          }),
        ]
      : []),
    colHelper.accessor(({ receivable }) => receivable?.purchaseOrder?.name, {
      title: "PO",
      width: TableCellWidth.Small,
    }),
    colHelper.money(
      ({ receivable }) => {
        const original = Money.fromPB(receivable?.original)
        return original.isPositive() ? original : undefined
      },
      {
        title: "Debit",
        width: TableCellWidth.Small,
      }
    ),
    colHelper.money(
      ({ receivable }) => {
        const original = Money.fromPB(receivable?.original)
        return original.isNegative() ? original.negative() : undefined
      },
      {
        title: "Credit",
        width: TableCellWidth.Small,
      }
    ),
    colHelper.data({
      title: "Running",
      width: TableCellWidth.Small,
      renderCell: ({ receivable }) => (
        <ClickableBalance
          balance={Money.fromPB(receivable?.runningBalance)}
          onClick={() =>
            openHistoryModal(
              UUID.fromPBOrThrow(receivable?.id, "Missing receivable.id")
            )
          }
        />
      ),
    }),
  ]
}

interface TransactionDetails {
  type: string
  color: ChipColor
  href: WebUrl | string
}
export function getTransactionDetails(type: RecvTxnType, txnID?: UUID) {
  const dataByType: Record<RecvTxnType, TransactionDetails> = {
    [RecvTxnType.INVOICE]: {
      type: "Invoice",
      color: "green",
      href: urls.sale(txnID),
    },
    [RecvTxnType.BILL_PAYMENT]: {
      type: "Bill Payment",
      color: "green",
      href: urls.bill(txnID),
    },
    [RecvTxnType.RETURN]: {
      type: "Return",
      color: "yellow",
      href: urls.sale(txnID),
    },
    [RecvTxnType.BILL_REFUND]: {
      type: "Bill Refund",
      color: "yellow",
      href: urls.bill(txnID, true),
    },
    [RecvTxnType.FINANCE_CHARGE]: {
      type: "Finance Charge",
      color: "green",
      href: urls.financeCharge(txnID),
    },
    [RecvTxnType.UNDEFINED]: {
      type: "",
      color: "gray",
      href: "",
    },
    [RecvTxnType.DISCOUNT]: {
      type: "Discount",
      color: "green",
      href: "",
    },
    [RecvTxnType.DISCOUNT_REVERSAL]: {
      type: "Discount Reversal",
      color: "yellow",
      href: "",
    },
    [RecvTxnType.DEPOSIT_REDEMPTION]: {
      type: "Deposit Redemption",
      color: "green",
      href: urls.sale(txnID),
    },
  }
  return dataByType[type]
}

export function getCombinedType(
  kind: ReceivableKind,
  type: RecvTxnType
): string {
  const breakdownName = getTransactionTypeName(type)
  if (kind === ReceivableKinds.CREDIT) {
    return "Credit - " + breakdownName
  }
  return breakdownName
}

export function getTransactionTypeName(type: RecvTxnType): string {
  return getTransactionDetails(type).type
}

function useHistoryTableFilters(customerId: UUID) {
  const poFilter = usePurchaseOrderFilter({ customerId: customerId })
  const typeFilter = useEnumFilter({
    key: "type",
    label: "Type",
    enumeration: RecvTxnType,
    labelExtractor: (type: RecvTxnType) => getTransactionDetails(type).type,
    useFilter: useWebFilter,
  })

  return {
    filters: [typeFilter.filter, poFilter.filter],
    selectedPurchaseOrderIds: poFilter.selectedPurchaseOrderIds,
    selectedTypes: typeFilter.selected,
  }
}
